import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import Product from '../../models/product';

const ComboComponentManager = ({ productId, availableProducts, onComponentsChange }) => {
  const [components, setComponents] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingComponentId, setEditingComponentId] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');

  // Load existing components if editing
  useEffect(() => {
    if (productId) {
      loadComponents();
    }
  }, [productId]);

  const loadComponents = async () => {
    const product = new Product();
    const data = await product.getComboComponents(productId);
    setComponents(data);
    if (onComponentsChange) {
      onComponentsChange(data);
    }
  };

  const handleAdd = async () => {
    if (!selectedProduct || !quantity || quantity <= 0) {
      toast.error('Vui lòng chọn sản phẩm và nhập số lượng hợp lệ', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    if (!productId) {
      // If creating new product, just add to local state
      const product = availableProducts.find(p => p.id === selectedProduct);
      const newComponent = {
        id: Date.now(), // temporary ID
        component_product_id: selectedProduct,
        quantity: parseFloat(quantity),
        component_product: product,
      };
      const updatedComponents = [...components, newComponent];
      setComponents(updatedComponents);
      if (onComponentsChange) {
        onComponentsChange(updatedComponents);
      }
      setSelectedProduct('');
      setQuantity('');
      return;
    }

    // If editing existing product, call API
    setLoading(true);
    const product = new Product();
    const result = await product.addComponent(productId, selectedProduct, parseFloat(quantity));
    setLoading(false);

    if (result.success) {
      toast.success(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
      loadComponents();
      setSelectedProduct('');
      setQuantity('');
    } else {
      toast.error(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  const handleRemove = async (componentId) => {
    if (!productId) {
      // If creating new product, just remove from local state
      const updatedComponents = components.filter(c => c.id !== componentId);
      setComponents(updatedComponents);
      if (onComponentsChange) {
        onComponentsChange(updatedComponents);
      }
      return;
    }

    // If editing existing product, call API
    setLoading(true);
    const product = new Product();
    const result = await product.removeComponent(productId, componentId);
    setLoading(false);

    if (result.success) {
      toast.success(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
      loadComponents();
    } else {
      toast.error(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  const handleStartEdit = (component) => {
    setEditingComponentId(component.id);
    setEditQuantity(component.quantity.toString());
  };

  const handleCancelEdit = () => {
    setEditingComponentId(null);
    setEditQuantity('');
  };

  const handleSaveEdit = async (componentId) => {
    if (!editQuantity || parseFloat(editQuantity) <= 0) {
      toast.error('Số lượng phải lớn hơn 0', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    if (!productId) {
      // If creating new product, update local state
      const updatedComponents = components.map(c =>
        c.id === componentId ? { ...c, quantity: parseFloat(editQuantity) } : c
      );
      setComponents(updatedComponents);
      if (onComponentsChange) {
        onComponentsChange(updatedComponents);
      }
      setEditingComponentId(null);
      setEditQuantity('');
      return;
    }

    // If editing existing product, call API
    setLoading(true);
    const product = new Product();
    const result = await product.updateComponent(productId, componentId, parseFloat(editQuantity));
    setLoading(false);

    if (result.success) {
      toast.success(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
      loadComponents();
      setEditingComponentId(null);
      setEditQuantity('');
    } else {
      toast.error(result.message, {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  // Filter out combo products, inactive products, and already-added components
  const filteredProducts = availableProducts.filter(p =>
    !p.is_combo &&
    p.status === 'ACTIVE' &&
    !components.find(c => c.component_product_id === p.id)
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Thành phần Combo
      </Typography>

      {components.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Thêm ít nhất một sản phẩm thành phần cho combo này
        </Alert>
      )}

      {/* Add Component Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <Autocomplete
            sx={{ minWidth: 300 }}
            options={filteredProducts}
            getOptionLabel={(option) => option.name || ''}
            value={filteredProducts.find(p => p.id === selectedProduct) || null}
            onChange={(event, newValue) => setSelectedProduct(newValue ? newValue.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Chọn sản phẩm"
                placeholder="Tìm kiếm sản phẩm..."
              />
            )}
            noOptionsText="Không tìm thấy sản phẩm"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <TextField
            label="Số lượng"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 0, step: 0.1 }}
            sx={{ width: 150 }}
          />

          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!selectedProduct || !quantity || quantity <= 0 || loading}
          >
            Thêm thành phần
          </Button>
        </Box>
      </Paper>

      {/* Components Table */}
      {components.length > 0 && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sản phẩm</TableCell>
                <TableCell>Số lượng</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map(component => (
                <TableRow key={component.id}>
                  <TableCell>
                    {component.component_product?.name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {editingComponentId === component.id ? (
                      <TextField
                        type="number"
                        value={editQuantity}
                        onChange={(e) => setEditQuantity(e.target.value)}
                        inputProps={{ min: 0, step: 0.1 }}
                        size="small"
                        sx={{ width: 100 }}
                        autoFocus
                      />
                    ) : (
                      component.quantity
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingComponentId === component.id ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(component.id)}
                          disabled={loading}
                          color="primary"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={loading}
                          color="default"
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(component)}
                          disabled={loading}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(component.id)}
                          disabled={loading}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default ComboComponentManager;
